import React, { ReactNode, useState } from 'react'
import { useTranslation } from 'language/translator'

import { UnitPrefix } from 'types/GlobalKpiOption'

import { getUnitLabel } from 'utils/unitLabel'

import Switch from 'components_new/atoms/Switch'

import SettingsDrawer from 'components_new/molecules/SettingsDrawer'
import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import { TInputType } from 'components_new/molecules/SettingsEditDialog/utils'

import {
  TEquivalent,
  getHelpText,
  getPreviewValue,
  getUnitPrefixOptions
} from './utils'
import { Option } from 'components_new/molecules/SelectSearch'

interface UnitSectionProps {
  onClose: () => void
  onUpdate: (body: {
    auto_format?: boolean
    equivalent?: TEquivalent
    number_of_decimals?: number
    unit_prefix?: UnitPrefix | null
  }) => void
  unit: string | null
  unitPrefix: {
    selected: UnitPrefix | null
    options: { value: UnitPrefix; label: string }[]
  }

  // NOT REQUIRED PROPS

  // the setting is decided elsewhere, and the text says where
  disabled?: boolean
  disabledText?: ReactNode

  // rewrites the values in another unit, which only a metric dataset
  // carries settings for
  equivalent?: TEquivalent

  // picks the unit and decimals from the value and locks the rest
  autoFormat?: boolean

  // shows the decimals in this section, optionally locked with a reason
  numberOfDecimals?: number
  decimalsDisabledText?: ReactNode
}

const UnitSection = (props: UnitSectionProps) => {
  const {
    autoFormat,
    decimalsDisabledText,
    disabled,
    disabledText,
    equivalent,
    numberOfDecimals,
    onClose,
    onUpdate,
    unit,
    unitPrefix
  } = props
  const { t } = useTranslation()

  /*-- drawer --*/
  const [drawer, setDrawer] = useState<{
    open: boolean
    title: string
  }>({
    open: false,
    title: ''
  })

  const handleBackDrawer = () => {
    setDrawer((prevState) => ({ ...prevState, open: false }))
  }

  const handleCloseDrawer = () => {
    handleBackDrawer()
    onClose()
  }

  const handleOpenDrawer = () => {
    setDrawer({
      open: true,
      title: t('widgets', 'equivalent_title')
    })
  }

  /*-- dialogs --*/
  const [dialog, setDialog] = useState<{
    open: boolean
    property: TProperty
    title: string
    data: {
      options: Option[]
      value: string
      type: TInputType
    }
  }>({
    open: false,
    property: 'factor',
    title: '',
    data: {
      options: [],
      value: '',
      type: 'text'
    }
  })

  // Properties edited in this component.
  type TProperty = 'factor' | 'number_of_decimals' | 'unit' | 'unit_prefix'

  const getPropertyTitle = (property: TProperty) => {
    switch (property) {
    case 'factor':
      return t('widgets', 'factor')
    case 'number_of_decimals':
      return t('widgets', 'number_of_decimals')
    case 'unit':
      return t('widgets', 'new_unit')
    case 'unit_prefix':
      return t('global', 'unit')
    }
  }

  const handleCloseDialog = () => {
    setDialog((prevState) => ({ ...prevState, open: false }))
  }

  const handleOpenDialog = (property: TProperty) => {
    let inputValue = ''
    let inputType: TInputType = 'text'
    let inputOptions: Option[] = []

    switch (property) {
    case 'factor':
      inputValue = equivalent?.factor?.toString() ?? ''
      inputType = 'decimal'
      break
    case 'number_of_decimals':
      inputValue = (numberOfDecimals ?? 0).toString()
      inputType = 'number'
      break
    case 'unit':
      inputValue = equivalent?.unit ?? ''
      inputType = 'text'
      break
    case 'unit_prefix':
      inputValue = unitPrefix.selected ?? ''
      inputType = 'select'
      inputOptions = getUnitPrefixOptions(unit, unitPrefix, t)
      break
    }

    setDialog({
      open: true,
      property: property,
      title: getPropertyTitle(property),
      data: {
        options: inputOptions,
        type: inputType,
        value: inputValue
      }
    })
  }

  const handleEditDialogValue = (value: string) => {
    setDialog((prevState) => ({
      ...prevState,
      data: { ...prevState.data, value }
    }))
  }

  const handleSubmitDialog = () => {
    switch (dialog.property) {
    case 'factor':
      onUpdate({
        equivalent: {
          unit: equivalent?.unit ?? null,
          factor: parseFloat(dialog.data.value)
        }
      })
      break
    case 'number_of_decimals':
      onUpdate({ number_of_decimals: parseInt(dialog.data.value) })
      break
    case 'unit':
      onUpdate({
        equivalent: {
          factor: equivalent?.factor ?? null,
          unit: dialog.data.value || null
        }
      })
      break
    case 'unit_prefix':
      onUpdate({
        unit_prefix:
            dialog.data.value === '' ? null : (dialog.data.value as UnitPrefix)
      })
      break
    }
  }

  const unitLabel = getUnitLabel(unit, unitPrefix)

  const unitPrefixOptions = unitPrefix.options

  const autoFormatText = autoFormat
    ? t('widgets', 'disabled_by_auto_format')
    : undefined
  const showsAutoFormat = autoFormat !== undefined
  const showsDecimals = numberOfDecimals !== undefined

  return (
    <>
      <SettingsGroup
        helperText={
          showsAutoFormat ? t('widgets', 'auto_format_helper') : undefined
        }
        title={t(
          'widgets',
          showsDecimals ? 'units_and_rounding_title' : 'units_title'
        )}
      >
        {showsAutoFormat ? (
          <SettingsItem
            actions={
              <Switch
                checked={autoFormat}
                onChange={() => onUpdate({ auto_format: !autoFormat })}
                size="small"
              />
            }
            title={t('widgets', 'auto_format')}
          />
        ) : null}
        <SettingsItem
          disabled={disabled || autoFormat || unitPrefixOptions.length === 0}
          disabledText={disabled ? disabledText : autoFormatText}
          onClick={() => handleOpenDialog('unit_prefix')}
          title={t('global', 'unit')}
          value={
            autoFormat
              ? t('global', 'auto')
              : unit === '%'
                ? '%'
                : unitLabel || ''
          }
          variant="edit"
        />
        {equivalent ? (
          <SettingsItem
            disabled={autoFormat}
            disabledText={autoFormatText}
            onClick={() => handleOpenDrawer()}
            title={t('widgets', 'equivalent_title')}
            value={getPreviewValue(unitLabel, equivalent)}
          />
        ) : null}
        {showsDecimals ? (
          <SettingsItem
            disabled={autoFormat || !!decimalsDisabledText}
            disabledText={decimalsDisabledText ?? autoFormatText}
            onClick={() => handleOpenDialog('number_of_decimals')}
            title={t('widgets', 'number_of_decimals')}
            value={autoFormat ? t('global', 'auto') : numberOfDecimals}
            variant="edit"
          />
        ) : null}
      </SettingsGroup>

      {/*-- drawer --*/}
      {equivalent ? (
        <SettingsDrawer
          onBack={handleBackDrawer}
          onClose={handleCloseDrawer}
          open={drawer.open}
          title={drawer.title}
        >
          <SettingsGroup helperText={getHelpText(unitLabel, equivalent, t)}>
            <SettingsItem
              onClick={() => handleOpenDialog('factor')}
              title={getPropertyTitle('factor')}
              value={equivalent.factor}
              variant="edit"
            />
            <SettingsItem
              onClick={() => handleOpenDialog('unit')}
              title={getPropertyTitle('unit')}
              value={equivalent.unit}
              variant="edit"
            />
          </SettingsGroup>
          <SettingsGroup>
            <SettingsItem
              color="info"
              onClick={() =>
                onUpdate({
                  equivalent: {
                    factor: null,
                    unit: null
                  }
                })
              }
              title={t('global', 'reset')}
              variant="action"
            />
          </SettingsGroup>
        </SettingsDrawer>
      ) : null}

      {/*-- dialogs --*/}
      <SettingsEditDialog
        clearValue={dialog.property === 'number_of_decimals' ? '0' : undefined}
        maxNumber={dialog.property === 'number_of_decimals' ? 20 : undefined}
        onChange={handleEditDialogValue}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        open={dialog.open}
        options={dialog.data.options}
        title={dialog.title}
        type={dialog.data.type}
        value={dialog.data.value}
      />
    </>
  )
}

export default UnitSection
